import { RefObject, useEffect, useRef } from "react";
import { observeCard } from "../../utils/events/cardObserver";
import { productCardEvents } from "./productCardEvents";
import { useProductCardEvent } from "./ProductCardEventContext";

type Props = {
  eventsRef: RefObject<HTMLElement | null>;
};

/**
 * Registers a card's root element with the shared IntersectionObserver and
 * dispatches a `card-in-view` event the first time the card is ~100% visible.
 * Renders nothing. Must be used within a ProductCardEventProvider.
 */
export function CardInViewTracker({ eventsRef }: Props): null {
  const dispatch = useProductCardEvent();

  // useProductCardEvent returns a new function each render; pin it so the
  // effect doesn't re-run and re-observe an already-fired card.
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(
    function observeCardInView() {
      const element = eventsRef.current;
      if (!element) return;
      return observeCard(element, () =>
        dispatchRef.current(productCardEvents.cardInView),
      );
    },
    [eventsRef],
  );

  return null;
}
